import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Search, Scale, FileText, Users, Clock, CheckSquare, Plus, 
  Command, Zap, TrendingUp, Building2, User, MessageCircle 
} from 'lucide-react';
import { auth } from '../utils/auth';

interface CommandItem {
  id: string;
  title: string;
  subtitle?: string;
  icon: any;
  color: string;
  action: () => void;
  category: string;
  badge?: string;
}

const CommandPalette = ({ isOpen, onClose, cases, clients, onSelectCase }: { isOpen: boolean; onClose: () => void; cases?: any[]; clients?: any[]; onSelectCase: (caseId: any) => void }) => {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);

  useEffect(() => {
    if (isOpen) {
      setQuery('');
      setSelectedIndex(0);
    }
  }, [isOpen]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isOpen) return;
      
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex((i) => Math.min(i + 1, filteredItems.length - 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex((i) => Math.max(i - 1, 0));
      } else if (e.key === 'Enter') {
        e.preventDefault();
        if (filteredItems[selectedIndex]) {
          executeAction(filteredItems[selectedIndex]);
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, selectedIndex, query]);

  const quickActions = [
    { 
      id: 'new-case', 
      title: 'Opprett ny sak', 
      icon: Plus, 
      color: 'purple',
      action: () => navigate('/lexy/case/new'),
      category: 'Actions'
    },
    { 
      id: 'new-client', 
      title: 'Opprett ny klient', 
      icon: Plus, 
      color: 'blue',
      action: () => navigate('/lexy/clients/new'),
      category: 'Actions'
    },
    { 
      id: 'overview', 
      title: 'Gå til oversikt', 
      icon: TrendingUp, 
      color: 'emerald',
      action: () => navigate('/lexy/dashboard'),
      category: 'Navigation'
    },
    { 
      id: 'cases', 
      title: 'Gå til saker', 
      icon: FileText, 
      color: 'blue',
      action: () => navigate('/lexy/dashboard?tab=cases'),
      category: 'Navigation'
    },
    { 
      id: 'clients', 
      title: 'Gå til klienter', 
      icon: Users, 
      color: 'purple',
      action: () => navigate('/lexy/dashboard?tab=clients'),
      category: 'Navigation'
    }
  ];

  const caseItems = (cases || []).map(c => ({
    id: `case-${c.id}`,
    title: c.topic || c.title,
    subtitle: (c.intake_summary || c.ai_summary || c.description || '').substring(0, 60) + '...',
    icon: FileText,
    color: c.source === 'MARKETPLACE' ? 'orange' : 'purple',
    action: () => {
      if (onSelectCase) {
        onSelectCase(c.id);
      } else if (c.source === 'MARKETPLACE') {
        navigate(`/marketplace/case/${c.id}`);
      } else {
        navigate(`/lexy/case/${c.id}/workspace`);
      }
      onClose();
    },
    category: 'Saker',
    badge: c.source === 'MARKETPLACE' ? '🌐' : '⚖️'
  }));

  const clientItems = (clients || []).map(c => ({
    id: `client-${c.id}`,
    title: c.type === 'PERSON' ? c.person_name : c.company_name,
    subtitle: c.email || c.org_nr,
    icon: c.type === 'COMPANY' ? Building2 : User,
    color: c.type === 'COMPANY' ? 'blue' : 'purple',
    action: () => {
      onClose();
      // Trigger client panel from dashboard
      navigate(`/lexy/dashboard?tab=clients&open=${c.id}`);
    },
    category: 'Klienter',
    badge: c.type === 'COMPANY' ? '🏢' : '👤'
  }));

  const allItems: CommandItem[] = [...quickActions, ...caseItems, ...clientItems];

  const filteredItems = query === '' 
    ? allItems.slice(0, 10)
    : allItems.filter(item => 
        item.title.toLowerCase().includes(query.toLowerCase()) ||
        (item.subtitle?.toLowerCase().includes(query.toLowerCase()))
      ).slice(0, 10);

  const executeAction = (item: CommandItem) => {
    item.action();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-gray-900/50 backdrop-blur-sm z-50 animate-in fade-in duration-200"
        onClick={onClose}
      />
      
      {/* Command Palette */}
      <div className="fixed top-[15%] left-1/2 -trangray-x-1/2 w-full max-w-2xl z-50 px-4 animate-in zoom-in-95 fade-in duration-200">
        <div className="bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden">
          {/* Search Input */}
          <div className="flex items-center gap-3 px-4 py-4 border-b border-gray-200">
            <Search className="w-5 h-5 text-gray-400" />
            <input
              type="text"
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setSelectedIndex(0);
              }}
              placeholder="Søk i saker, klienter eller utfør handlinger..."
              className="flex-1 bg-transparent text-gray-900 placeholder-gray-400 focus:outline-none text-lg"
              autoFocus
            />
            <kbd className="px-2 py-1 bg-gray-100 rounded text-xs text-gray-600 font-mono">
              ESC
            </kbd>
          </div>
          
          {/* Results */}
          <div className="max-h-[500px] overflow-y-auto">
            {filteredItems.length === 0 ? (
              <div className="p-8 text-center">
                <p className="text-gray-600">Ingen resultater</p>
              </div>
            ) : (
              <div className="p-2">
                {/* Group by category */}
                {['Actions', 'Navigation', 'Saker', 'Klienter'].map(category => {
                  const items = filteredItems.filter(i => i.category === category);
                  if (items.length === 0) return null;
                  
                  return (
                    <div key={category} className="mb-2">
                      <div className="px-3 py-2 text-xs font-bold text-gray-500 uppercase">
                        {category}
                      </div>
                      {items.map((item, index) => {
                        const globalIndex = filteredItems.indexOf(item);
                        const isSelected = globalIndex === selectedIndex;
                        const Icon = item.icon;
                        
                        return (
                          <button
                            key={item.id}
                            onClick={() => executeAction(item)}
                            onMouseEnter={() => setSelectedIndex(globalIndex)}
                            className={`w-full flex items-center gap-3 px-3 py-3 rounded-lg transition-all ${
                              isSelected 
                                ? 'bg-gray-100 border border-gray-200' 
                                : 'hover:bg-gray-50'
                            }`}
                          >
                            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                              item.color === 'purple' ? 'bg-purple-100' :
                              item.color === 'blue' ? 'bg-blue-100' :
                              item.color === 'emerald' ? 'bg-emerald-100' :
                              'bg-gray-100'
                            }`}>
                              <Icon className={`w-5 h-5 ${
                                item.color === 'purple' ? 'text-purple-600' :
                                item.color === 'blue' ? 'text-blue-600' :
                                item.color === 'emerald' ? 'text-emerald-600' :
                                'text-gray-600'
                              }`} />
                            </div>
                            
                            <div className="flex-1 text-left">
                              <div className="flex items-center gap-2">
                                <p className="font-medium text-gray-900">{item.title}</p>
                                {item.badge && (
                                  <span className="text-xs">{item.badge}</span>
                                )}
                              </div>
                              {item.subtitle && (
                                <p className="text-sm text-gray-500">{item.subtitle}</p>
                              )}
                            </div>
                            
                            {isSelected && (
                              <kbd className="px-2 py-1 bg-white rounded text-xs text-gray-600 font-mono border border-gray-200">
                                ↵
                              </kbd>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          
          {/* Footer */}
          <div className="px-4 py-3 bg-gray-50 border-t border-gray-200 flex items-center justify-between text-xs text-gray-600">
            <div className="flex items-center gap-4">
              <span className="flex items-center gap-1">
                <kbd className="px-1.5 py-0.5 bg-white rounded border border-gray-300 font-mono">↑</kbd>
                <kbd className="px-1.5 py-0.5 bg-white rounded border border-gray-300 font-mono">↓</kbd>
                navigér
              </span>
              <span className="flex items-center gap-1">
                <kbd className="px-1.5 py-0.5 bg-white rounded border border-gray-300 font-mono">↵</kbd>
                velg
              </span>
            </div>
            <span>Cmd+K for å åpne igjen</span>
          </div>
        </div>
      </div>
    </>
  );
};

export default CommandPalette;
